import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { reportError } from "@/lib/monitoring";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportError(error, {
      source: "AppErrorBoundary",
      componentStack: errorInfo.componentStack,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-muted px-6">
        <div className="w-full max-w-md rounded-2xl border bg-background p-8 shadow-sm">
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Application Error
          </p>
          <h1 className="mt-3 text-2xl font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            The app hit an unexpected error. Reload to recover. If this keeps
            happening, check the configured error reporting endpoint.
          </p>
          <Button className="mt-6 w-full" onClick={this.handleReload}>
            Reload app
          </Button>
        </div>
      </div>
    );
  }
}
